import { type ClipboardEventHandler, forwardRef } from 'react'

import BaseSearch from './base'

export interface SearchProps {
  debounceDelay?: number
  includeIcon?: boolean
  minInputLengthForAutocomplete?: number
  // onInputChange can return list of autocomplete results or nothing (if no autocomplete)
  // biome-ignore lint/suspicious/noConfusingVoidType: Bulk suppression as part of biome migration
  onInputChange: (term: string) => Promise<string[] | void>
  onSubmit?: (term: string) => void
  placeholder?: string
  showAutocomplete?: boolean
  value?: string
  onClear?: () => void
  onPaste?: ClipboardEventHandler<HTMLInputElement | undefined>
  disabled?: boolean
}

const DEFAULT_MIN_SEARCH_LENGTH = 0
const DEFAULT_DEBOUNCE_TIME = 250

const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      debounceDelay = DEFAULT_DEBOUNCE_TIME,
      includeIcon = true,
      minInputLengthForAutocomplete = DEFAULT_MIN_SEARCH_LENGTH,
      onInputChange = () => Promise.resolve(),
      onSubmit = () => Promise.resolve(),
      placeholder = 'Search...',
      showAutocomplete = false,
      value,
      onClear,
      onPaste,
      disabled,
      ...props
    },
    // Keeping ref for Input in BaseSearch available via forwardRef if needed
    _ref,
  ) => {
    return (
      <BaseSearch
        debounceDelay={debounceDelay}
        includeIcon={includeIcon}
        minInputLengthForAutocomplete={minInputLengthForAutocomplete}
        onInputChange={onInputChange}
        onSubmit={onSubmit}
        placeholder={placeholder}
        showAutocomplete={showAutocomplete}
        value={value ?? ''}
        onClear={onClear}
        onPaste={onPaste}
        disabled={disabled}
        {...props}
      />
    )
  },
)

Search.displayName = 'Search'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default Search
